import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { useSoundEffects } from "@thehoneyjar/sound-engine";
import { cn } from "@/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        black: "bg-black/80 text-white hover:bg-black/50",
        white: "bg-white text-black hover:bg-white/90 disabled:bg-white/10 disabled:text-white",
        "action-yellow":
          "relative bg-gradient-to-b from-[#F2C744] to-[#D4A62E] text-black font-pressStart2P text-xs uppercase shadow-[inset_0_2px_0_0_rgba(255,255,255,0.4),inset_0_-2px_0_0_rgba(0,0,0,0.3)] hover:from-[#FFD43B] hover:to-[#F2C744] active:shadow-[inset_0_2px_0_0_rgba(0,0,0,0.3),0_1px_2px_rgba(0,0,0,0.2)] active:translate-y-[2px] transition-all duration-150 border-2 border-black/30 rounded-xl [text-shadow:0_2px_4px_rgba(0,0,0,0.3)]",
        "action-purple":
          "relative bg-gradient-to-b from-[#C02EF0] to-[#7A3FD4] text-white font-pressStart2P text-xs uppercase shadow-[inset_0_2px_0_0_rgba(255,255,255,0.4),inset_0_-2px_0_0_rgba(0,0,0,0.3)] hover:from-[#D65FFF] hover:to-[#C02EF0] active:shadow-[inset_0_2px_0_0_rgba(0,0,0,0.3),0_1px_2px_rgba(0,0,0,0.2)] active:translate-y-[2px] transition-all duration-150 border-2 border-black/30 rounded-xl [text-shadow:0_2px_4px_rgba(0,0,0,0.3)]",
        "action-pink":
          "relative bg-gradient-to-b from-[#FF72B2] to-[#E550A0] text-white font-pressStart2P text-xs uppercase shadow-[inset_0_2px_0_0_rgba(255,255,255,0.4),inset_0_-2px_0_0_rgba(0,0,0,0.3)] hover:from-[#FF82C4] hover:to-[#FF72B2] active:shadow-[inset_0_2px_0_0_rgba(0,0,0,0.3),0_1px_2px_rgba(0,0,0,0.2)] active:translate-y-[2px] transition-all duration-150 border-2 border-black/30 rounded-xl [text-shadow:0_2px_4px_rgba(0,0,0,0.3)]",
        "action-blue":
          "relative bg-gradient-to-b from-[#3E61FA] to-[#2E4FD8] text-white font-pressStart2P text-xs uppercase shadow-[inset_0_2px_0_0_rgba(255,255,255,0.4),inset_0_-2px_0_0_rgba(0,0,0,0.3)] hover:from-[#5474FB] hover:to-[#3E61FA] active:shadow-[inset_0_2px_0_0_rgba(0,0,0,0.3),0_1px_2px_rgba(0,0,0,0.2)] active:translate-y-[2px] transition-all duration-150 border-2 border-black/30 rounded-xl [text-shadow:0_2px_4px_rgba(0,0,0,0.3)]",
        "action-green":
          "relative bg-gradient-to-b from-[#63DF77] to-[#4FB85B] text-black font-pressStart2P text-xs uppercase shadow-[inset_0_2px_0_0_rgba(255,255,255,0.4),inset_0_-2px_0_0_rgba(0,0,0,0.3)] hover:from-[#7FE88D] hover:to-[#63DF77] active:shadow-[inset_0_2px_0_0_rgba(0,0,0,0.3),0_1px_2px_rgba(0,0,0,0.2)] active:translate-y-[2px] transition-all duration-150 border-2 border-black/30 rounded-xl [text-shadow:0_2px_4px_rgba(0,0,0,0.3)]",
        "action-red":
          "relative bg-gradient-to-b from-[#FF4B48] to-[#D63330] text-white font-pressStart2P text-xs uppercase shadow-[inset_0_2px_0_0_rgba(255,255,255,0.4),inset_0_-2px_0_0_rgba(0,0,0,0.3)] hover:from-[#FF6B68] hover:to-[#FF4B48] active:shadow-[inset_0_2px_0_0_rgba(0,0,0,0.3),0_1px_2px_rgba(0,0,0,0.2)] active:translate-y-[2px] transition-all duration-150 border-2 border-black/30 rounded-xl [text-shadow:0_2px_4px_rgba(0,0,0,0.3)]",
        "action-orange":
          "relative bg-gradient-to-b from-[#FFA648] to-[#E68A3C] text-black font-pressStart2P text-xs uppercase shadow-[inset_0_2px_0_0_rgba(255,255,255,0.4),inset_0_-2px_0_0_rgba(0,0,0,0.3)] hover:from-[#FFB668] hover:to-[#FFA648] active:shadow-[inset_0_2px_0_0_rgba(0,0,0,0.3),0_1px_2px_rgba(0,0,0,0.2)] active:translate-y-[2px] transition-all duration-150 border-2 border-black/30 rounded-xl [text-shadow:0_2px_4px_rgba(0,0,0,0.3)]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, onMouseEnter, ...props }, ref) => {
    const { playSound } = useSoundEffects();
    const Comp = (asChild ? Slot : "button") as React.ElementType;

    // Check if sounds are enabled (default to true if not set)
    const soundEnabled =
      typeof window !== "undefined" ? localStorage.getItem("soundEnabled") !== "false" : true;

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        // Play sound for action-blue buttons only
        if (soundEnabled && variant === "action-blue" && !props.disabled) {
          void playSound("ctaButtonClick");
        }
        onClick?.(e);
      },
      [onClick, variant, props.disabled, soundEnabled, playSound],
    );

    const handleMouseEnter = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        // Play hover sound for action-blue buttons only
        if (soundEnabled && variant === "action-blue" && !props.disabled) {
          void playSound("ctaButtonHover");
        }
        onMouseEnter?.(e);
      },
      [onMouseEnter, variant, props.disabled, soundEnabled, playSound],
    );

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
